import { ReactNode, useContext, useEffect, useState } from "react";
import Papa from "papaparse";
import { Box, Typography, Stack, List } from "@mui/material";
import { useTranslation } from "react-i18next";
import ImportAlert, { ImportAlertProps } from "components/importer/ImportAlert";
import { CloudDownload, Save } from "@mui/icons-material";
import { Button } from "@mui/material";
import useAlerts from "./useImportAlerts";
import dayjs from "dayjs";
import { SavedImportMapping } from "types";
import { ScrollableFormContainer } from "components/layout/ScrollableContainer";
import { ImportWizardContext } from "components/importer/ImportWizardContext";

interface CompleteStepProps {
  saveColumnMappings: (mappings: SavedImportMapping) => void;
}
const CompleteStep = ({ saveColumnMappings }: CompleteStepProps): ReactNode => {
  const { t } = useTranslation();
  const { state } = useContext(ImportWizardContext);
  const ALERTS = useAlerts({
    discardedRows: state.discardedRows.length,
    successfulImports: state.csvData.length - state.discardedRows.length,
    entityName: state.entityName,
  });
  const mappingName = `${state.fileName}_${dayjs(new Date()).format(
    "YYYY-MM-DD",
  )}`;
  const [centerAlerts, setCenterAlerts] = useState<ImportAlertProps[]>([
    ALERTS.IMPORT_REPORT,
  ]);

  // display alerts
  useEffect(() => {
    if (state.discardedRows.length > 0) {
      const applicableAlerts = [ALERTS.IMPORT_REPORT];
      applicableAlerts.push(ALERTS.DISCARDED_ROWS);
      setCenterAlerts(applicableAlerts);
    }
  }, []);

  // event handlers
  const handleSaveColumnMappings = () => {
    saveColumnMappings({
      name: mappingName,
      mappings: state.columnMappings,
    });
  };
  const handleDownloadDiscardedRows = () => {
    const discardedRows = state.csvData.filter((row, index) =>
      state.discardedRows.includes(index),
    );
    const csv = Papa.unparse({
      fields: state.csvHeaders,
      data: discardedRows,
    });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    if (link.download) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "discarded-rows.csv");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <Stack direction="row" spacing={6} display="flex" pt={2}>
      <Stack direction="column" spacing={4} flex={2} />
      <Stack direction="column" flex={3}>
        <ScrollableFormContainer maxHeight={`calc(100dvh - 372px)`}>
          <Stack direction="column" spacing={4}>
            <List disablePadding>
              {centerAlerts.map((props, index) => (
                <ImportAlert key={index} {...props} />
              ))}
            </List>
            <Box>
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSaveColumnMappings}
                fullWidth
                sx={{ py: 1.5 }}
              >
                <Typography variant="button" align="right" color="white">
                  {t("buttons.save", {
                    name: t("columnMappings", { ns: "importWizard" }),
                  })}
                </Typography>
              </Button>
              <Typography
                variant="caption"
                color={`white`}
                sx={{ display: "block", mt: 0.5, pl: 2 }}
              >
                {t("saveColumnMappingsHelper", {
                  ns: "importWizard",
                  name: mappingName,
                })}
              </Typography>
            </Box>
            <Box>
              <Button
                variant="contained"
                startIcon={<CloudDownload />}
                onClick={handleDownloadDiscardedRows}
                fullWidth
                disabled={state.discardedRows.length === 0}
                sx={{ py: 1.5 }}
              >
                {t("buttons.download", {
                  name: t("discardedRows", { ns: "importWizard" }),
                })}
              </Button>
              <Typography
                variant="caption"
                color={`#000000CC`}
                sx={{ display: "block", mt: 0.5, pl: 2 }}
              >
                {t("downloadDiscardedRowsHelper", { ns: "importWizard" })}
              </Typography>
            </Box>
          </Stack>
        </ScrollableFormContainer>
      </Stack>
      <Stack direction="column" spacing={0} flex={2} />
    </Stack>
  );
};

export default CompleteStep;
